import api from "./client";

import type Patient from "../types/Patient";
import type Disposition from "../types/Disposition";
import type PatientDisposition from "../types/PatientDisposition";
import type EDD from "../types/EDD";
import Configuration from "../types/Configuration";
import IntelligenceText from "../types/IntelligenceText";
import PatientMilestone from "../types/PatientMilestone";
import PatientClinicalInsight from "../types/PatientClinicalInsight";
import PatientInsights from "../types/PatientInsights";
import Milestone from "../types/Milestone";
import PatientConsideration from "../types/PatientConsideration";
import MedicallyReadyStatus from "../types/MedicallyReadyStatus";
import PatientInsightsQuoteScore from "../types/PatientInsightsQuoteScore";
import RoundingPatientEntry from "../types/RoundingPatientEntry";
import RoundingSession from "../types/RoundingSession";
import Department, { BoardSolution } from "../types/Department";
import DepartmentGroup from "../types/DepartmentGroup";
import PatientListEntry from "../types/PatientListEntry";
import PatientNote from "../types/PatientNote";
import UserPreferences from "../types/UserPreferences";
import ListFilter from "../types/ListFilter";
import QCardReviewStatusResponse, {
  QCardReviewStatusRequestParams,
} from "../types/QCardReviewStatus";

interface GetPatientParameters {
  emrPatientID: string;
  emrHospitalID: string;
  fhirPatientID: string;
}

const getPatient = async ({
  emrPatientID,
  emrHospitalID,
  fhirPatientID,
}: GetPatientParameters): Promise<Patient> => {
  const response = await api.get("/patients/", {
    params: {
      emr_patient_id: emrPatientID,
      emr_hospital_id: emrHospitalID,
      fhir_patient_id: fhirPatientID,
    },
  });
  return response?.data;
};

const getDispositions = async (): Promise<Disposition[]> => {
  // only retrieve relevant dispositions
  const response = await api.get(
    "/dispositions/?is_active=true&display_in_option_list=true"
  );
  return response?.data;
};

const getPatientDispositions = async (
  patientID: number,
  includeHistory: boolean
): Promise<PatientDisposition[]> => {
  const requestUrl = includeHistory
    ? `/patient-dispositions/?patient_id=${patientID}&include_history=true`
    : `/patient-dispositions/?patient_id=${patientID}`;
  const response = await api.get(requestUrl);
  return response?.data;
};

const listPatientDispositions = async (
  patientIDs: number[]
): Promise<Record<number, PatientDisposition[]>> => {
  const requestUrl = `/patient-dispositions/list/?patient_ids=${patientIDs}`;
  const response = await api.get(requestUrl);
  return response?.data;
};

const createdPatientDispositions = async (
  payload: Record<string, unknown>
): Promise<PatientDisposition> => {
  const response = await api.post("/patient-dispositions/", payload);
  return response.data;
};
const updatePatientDispositions = async (
  payload: Record<string, unknown>
): Promise<PatientDisposition> => {
  const response = await api.patch(`/patient-dispositions/${payload.id}/`, {
    status: payload.status,
    disposition_type: payload.disposition_type,
    disposition: payload.disposition,
    source: payload.source,
    suggested_patient_disposition_id: payload.suggested_patient_disposition_id,
    decision_updated_ts: payload.decision_updated_ts,
  });
  return response.data;
};

const putPatientDispositionsEvent = async (
  patientID: number
): Promise<PatientDisposition[]> => {
  const response = await api.put(
    `/patient-dispositions/send-event/?patient_id=${patientID}`
  );
  return response.data;
};

const deletePatientDispositions = async (
  id: number
): Promise<PatientDisposition> => {
  const response = await api.delete(`/patient-dispositions/${id}/`);
  return response.data;
};

const getEDDs = async (
  patientID: number,
  includeHistory: boolean
): Promise<EDD[]> => {
  const requestUrl = includeHistory
    ? `/edds/?patient_id=${patientID}&include_history=true`
    : `/edds/?patient_id=${patientID}`;
  const response = await api.get(requestUrl);
  return response?.data;
};

const listEDDs = async (
  patientIDs: number[]
): Promise<Record<number, EDD[]>> => {
  const requestUrl = `/edds/list/?patient_ids=${patientIDs}`;
  const response = await api.get(requestUrl);
  return response?.data;
};

const postEDD = (payload: EDD): Promise<EDD> =>
  api.post("/edds/", payload).then((response) => response.data);

function patchEDD(payload: Record<string, unknown>): Promise<EDD> {
  return api
    .patch(`/edds/${payload.id}/`, {
      edd_value: payload.edd_value,
      status: payload.status,
      source: payload.source,
      suggested_patient_edd_id: payload.suggested_patient_edd_id,
      decision_updated_ts: payload.decision_updated_ts,
      is_am_discharge: payload.is_am_discharge,
    })
    .then((response) => response.data);
}

const getMilestones = async (): Promise<Milestone[]> => {
  const response = await api.get("/milestones/");
  return response?.data;
};

const getPatientMilestones = async (
  patientID: number
): Promise<PatientMilestone[]> => {
  // we need all statuses except UNKNOWN to properly handle state transitions
  const response = await api.get(
    `/patient-milestones/?patient_id=${patientID}&should_display=true&milestone_status=["INCOMPLETE", "COMPLETED", "ALWAYS_NORMAL", "CANCELED"]`
  );

  return response?.data;
};

const listPatientMilestones = async (
  patientIDs: number[]
): Promise<Record<number, PatientMilestone[]>> => {
  // get all milestones so we can re-use the data for initial QCard open
  const response = await api.get(
    `/patient-milestones/list/?patient_ids=${patientIDs}&should_display=true&milestone_status=["INCOMPLETE", "COMPLETED", "ALWAYS_NORMAL", "CANCELED"]`
  );

  return response?.data;
};

const getPatientClinicalInsights = async (
  patientID: number
): Promise<PatientClinicalInsight[]> => {
  const response = await api.get(
    `/patient-clinical-insights/?patient_id=${patientID}`
  );
  return response?.data ?? [];
};

const listPatientClinicalInsights = async (
  patientIDs: number[]
): Promise<Record<number, PatientClinicalInsight[]>> => {
  const response = await api.get(
    `/patient-clinical-insights/list/?patient_ids=${patientIDs}`
  );
  return response?.data ?? {};
};

const postPatientMilestones = (
  payload: PatientMilestone[]
): Promise<PatientMilestone[]> => {
  return Promise.all(
    payload.map((pm) => api.post("/patient-milestones/", pm))
  ).then((response) => {
    return response.map((r) => r.data);
  });
};

function patchPatientMilestone(
  payload: Record<string, unknown>
): Promise<PatientMilestone> {
  return api
    .patch(`/patient-milestones/${payload.id}/`, {
      milestone_status: payload.milestone_status,
      secondary_formatted_value: payload.secondary_formatted_value,
    })
    .then((response) => response.data);
}

const getConfiguration = async (): Promise<Configuration[]> => {
  const response = await api.get(`/configuration/`);
  return response?.data;
};

const getIntelligenceText = async (
  patientID: number
): Promise<IntelligenceText[]> => {
  const response = await api.get(`/intelligence/?patient_id=${patientID}`);
  return response?.data;
};

const listIntelligenceText = async (
  patientIDs: number[]
): Promise<Record<number, IntelligenceText[]>> => {
  const response = await api.get(
    `/intelligence/list/?patient_ids=${patientIDs}`
  );
  return response?.data;
};

const hideIntelligenceText = (
  intelligenceTextID: number
): Promise<IntelligenceText> =>
  api
    .put(`/intelligence/${intelligenceTextID}/hide/`)
    .then((response) => response.data);

const getMedicallyReadyStatus = async (
  patientID: number
): Promise<MedicallyReadyStatus> => {
  const response = await api.get(`/medically-ready-status/${patientID}/`);
  return response?.data;
};

const listMedicallyReadyStatus = async (
  patientIDs: number[]
): Promise<Record<number, MedicallyReadyStatus>> => {
  const response = await api.get(
    `/medically-ready-status/list/?patient_ids=${patientIDs}`
  );
  return response?.data;
};

const postMedicallyReadyStatus = (
  payload: MedicallyReadyStatus
): Promise<MedicallyReadyStatus> =>
  api
    .post("/medically-ready-status/", payload)
    .then((response) => response.data);

function patchMedicallyReadyStatus(
  payload: Record<string, unknown>
): Promise<MedicallyReadyStatus> {
  return api
    .patch(`/medically-ready-status/${payload.patient_id}/`, {
      status: payload.status,
    })
    .then((response) => response.data);
}

const getPatientInsights = async (
  patientID: number
): Promise<PatientInsights> => {
  const response = await api.get(`/patient-insights/${patientID}/`);
  return response?.data;
};

const listPatientInsights = async (
  patientIDs: number[]
): Promise<Record<number, PatientInsights>> => {
  const response = await api.get(
    `/patient-insights/list/?patient_ids=${patientIDs}`
  );
  return response?.data;
};

const postPatientInsightsQuoteScore = (
  payload: PatientInsightsQuoteScore
): Promise<PatientInsightsQuoteScore> => {
  return api
    .post("/patient-insights-quote-scores/", payload)
    .then((response) => response.data);
};

const patchPatientInsightsQuoteScore = (
  payload: Record<string, unknown>
): Promise<PatientInsightsQuoteScore> => {
  return api
    .patch(`/patient-insights-quote-scores/${payload.id}/`, payload)
    .then((response) => response.data);
};

const getActiveRoundingSession = async (): Promise<RoundingSession> => {
  const response = await api.get(`/rounding-sessions/active-session/`);
  return response?.data;
};

const postRoundingSession = (): Promise<RoundingSession> =>
  api
    .post("/rounding-sessions/", { is_active: true })
    .then((response) => response.data);

function endRoundingSession(
  payload: Record<string, unknown>
): Promise<RoundingSession> {
  return api
    .put(`/rounding-sessions/${payload.id}/end-session/`, {
      end_time: payload.end_time,
    })
    .then((response) => response.data);
}

const postRoundingPatientEntry = (
  payload: RoundingPatientEntry
): Promise<RoundingPatientEntry> =>
  api
    .post("/rounding-patient-entries/", payload)
    .then((response) => response.data);

function patchRoundingPatientEntry(
  payload: Record<string, unknown>
): Promise<RoundingPatientEntry> {
  return api
    .patch(`/rounding-patient-entries/${payload.id}/`, {
      is_rounded: payload.is_rounded,
      rounded_timestamp: payload.rounded_timestamp,
      rounding_session: payload.rounding_session,
    })
    .then((response) => response.data);
}

const getPatientConsiderations = async (
  patientID: number
): Promise<PatientConsideration[]> => {
  const response = await api.get(
    `/patient-considerations/?patient_id=${patientID}`
  );
  return response?.data;
};

const patchPatientConsideration = (
  payload: Record<string, unknown>
): Promise<PatientConsideration> => {
  return api
    .patch(`/patient-considerations/${payload.id}/`, {
      status: payload.status,
    })
    .then((response) => response.data);
};

// The backend lists IP's boards unless asked for another solution's.
const getDepartments = async (
  solution?: BoardSolution
): Promise<Department[]> => {
  const response = await api.get("/departments/", { params: { solution } });
  return response?.data;
};

const getDepartmentGroups = async (
  solution?: BoardSolution
): Promise<DepartmentGroup[]> => {
  const response = await api.get("/department-groups/", {
    params: { solution },
  });
  return response?.data;
};

const getPatientList = async (
  department?: string,
  group?: string,
  hospitalID?: number
): Promise<PatientListEntry[]> => {
  const urlParams = [group ? `group=${group}` : `department=${department}`];

  if (hospitalID) {
    urlParams.push(`qv_hospital_id=${hospitalID}`);
  }

  const response = await api.get(`/patient-list/?${urlParams.join("&")}`);
  return response?.data;
};

const getPatientListFilters = async (
  department?: string,
  group?: string,
  hospitalID?: number
): Promise<ListFilter[]> => {
  const urlParams = [group ? `group=${group}` : `department=${department}`];

  if (hospitalID) {
    urlParams.push(`qv_hospital_id=${hospitalID}`);
  }

  const response = await api.get(
    `/patient-list/filters/?${urlParams.join("&")}`
  );
  return response?.data;
};

const getPatientNotes = async (patientID: number): Promise<PatientNote[]> => {
  const response = await api.get(`/patient-notes/?patient_id=${patientID}`);
  return response?.data;
};

const listPatientNotes = async (
  patientIDs: number[]
): Promise<Record<number, PatientNote[]>> => {
  const response = await api.get(
    `/patient-notes/list/?patient_ids=${patientIDs}`
  );
  return response?.data;
};

const postPatientNote = (payload: {
  created_by_email?: string;
  patient: number;
  content: string;
}): Promise<PatientNote> =>
  api.post("/patient-notes/", payload).then((response) => response.data);

const patchPatientNote = (
  payload: Record<string, unknown>
): Promise<PatientNote> => {
  return api
    .patch(`/patient-notes/${payload.id}/`, {
      content: payload.content,
      is_deleted: payload.is_deleted,
    })
    .then((response) => response.data);
};

const getUserPreferences = async (): Promise<UserPreferences> => {
  const response = await api.get("/user-preferences/");
  return response?.data;
};

const patchUserPreferences = (
  payload: Record<string, unknown>
): Promise<UserPreferences> => {
  return api
    .patch(`/user-preferences/${payload.id}/`, payload)
    .then((response) => response.data);
};

const getQCardReviewStatus = async ({
  emrPatientID,
  emrHospitalID,
  departmentName,
  timezone,
  qvHospitalID,
}: QCardReviewStatusRequestParams): Promise<QCardReviewStatusResponse | null> => {
  const response = await api.get("/qcard-review-status/", {
    params: {
      emr_patient_id: emrPatientID,
      emr_hospital_id: emrHospitalID,
      department_name: departmentName,
      timezone,
      ...(qvHospitalID != null ? { qv_hospital_id: qvHospitalID } : {}),
    },
  });
  return response?.data;
};

const postQCardOpen = async ({
  emrPatientID,
  emrHospitalID,
  departmentName,
  timezone,
  qvHospitalID,
}: QCardReviewStatusRequestParams): Promise<void> => {
  await api.post("/qcard-review-status/", {
    emr_patient_id: emrPatientID,
    emr_hospital_id: emrHospitalID,
    department_name: departmentName,
    timezone,
    ...(qvHospitalID != null ? { qv_hospital_id: qvHospitalID } : {}),
  });
};

export {
  getPatient,
  getDispositions,
  getEDDs,
  listEDDs,
  postEDD,
  patchEDD,
  getPatientDispositions,
  listPatientDispositions,
  createdPatientDispositions,
  updatePatientDispositions,
  putPatientDispositionsEvent,
  deletePatientDispositions,
  getMilestones,
  getPatientMilestones,
  listPatientMilestones,
  getPatientClinicalInsights,
  listPatientClinicalInsights,
  patchPatientMilestone,
  postPatientMilestones,
  getConfiguration,
  getIntelligenceText,
  listIntelligenceText,
  hideIntelligenceText,
  getMedicallyReadyStatus,
  listMedicallyReadyStatus,
  postMedicallyReadyStatus,
  patchMedicallyReadyStatus,
  getPatientInsights,
  listPatientInsights,
  postPatientInsightsQuoteScore,
  patchPatientInsightsQuoteScore,
  getActiveRoundingSession,
  postRoundingSession,
  endRoundingSession,
  postRoundingPatientEntry,
  patchRoundingPatientEntry,
  getPatientConsiderations,
  patchPatientConsideration,
  getDepartments,
  getDepartmentGroups,
  getPatientList,
  getPatientListFilters,
  getPatientNotes,
  listPatientNotes,
  postPatientNote,
  patchPatientNote,
  getUserPreferences,
  patchUserPreferences,
  getQCardReviewStatus,
  postQCardOpen,
};
